import { create } from 'zustand';
import { usePermissionsStore } from '@/stores/permissions';
import { getStoredTokens, isTokenExpired } from '@/auth/token';
import { extractUser } from '@/auth/oidc';
import { attachSentryUser } from '@/sentry';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  odooId: number | null;
  /** Dev personas only: a login time (epoch s) the SKIP_AUTH bypass passes on as `auth_time`. */
  authTime?: number;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: AuthUser) => void;
  updateToken: (accessToken: string, idToken: string) => void;
  clearAuth: () => void;
  hasRole: (role: string) => boolean;
}

/**
 * Apply an active admin role-override (production impersonation, ADR-018) to a
 * freshly-extracted JWT user. The override stores the impersonated persona's
 * odooId; without this, components that read `user.odooId` from the store (e.g.
 * MeerwerkenSection's subcontractor scope) keep the real admin's odooId and
 * silently query the wrong identity. Roles stay handled by `hasRole()` +
 * permissions; here we only need to carry the impersonated odooId. No-op in
 * dev mode (SKIP_AUTH swaps the whole user) and when no override is set.
 */
function applyRoleOverride(user: AuthUser): AuthUser {
  if (import.meta.env.VITE_SKIP_AUTH === 'true') return user;
  const override = localStorage.getItem('dev-role-override');
  if (!override) return user;
  try {
    const parsed = JSON.parse(override) as { odooId?: number | null };
    if ('odooId' in parsed && (typeof parsed.odooId === 'number' || parsed.odooId === null)) {
      return { ...user, odooId: parsed.odooId };
    }
  } catch {
    // Malformed override — leave the real user untouched
  }
  return user;
}

function getInitialAuth(): { token: string | null; user: AuthUser | null; isAuthenticated: boolean } {
  // Dev mode bypass — unchanged
  if (import.meta.env.VITE_SKIP_AUTH === 'true') {
    const saved = localStorage.getItem('dev-persona');
    const persona: AuthUser = saved ? JSON.parse(saved) : {
      id: 'dev',
      name: 'Dev User',
      email: 'dev@thermoduct.be',
      roles: ['admin'],
      odooId: null,
    };
    setTimeout(() => usePermissionsStore.getState().fetchPermissions(), 0);
    return { token: 'dev-token', user: persona, isAuthenticated: true };
  }

  // Production: try hydrating from localStorage (user may already be logged in)
  const { accessToken, idToken } = getStoredTokens();
  if (accessToken && idToken && !isTokenExpired(0)) {
    try {
      const user = applyRoleOverride(extractUser(accessToken, idToken));
      setTimeout(() => usePermissionsStore.getState().fetchPermissions(), 0);
      return { token: accessToken, user, isAuthenticated: true };
    } catch {
      // Token parsing failed — fall through to unauthenticated
    }
  }

  return { token: null, user: null, isAuthenticated: false };
}

const initial = getInitialAuth();

export const useAuthStore = create<AuthState>((set, get) => ({
  token: initial.token,
  user: initial.user,
  isAuthenticated: initial.isAuthenticated,

  setAuth: (token, user) => {
    set({ token, user, isAuthenticated: true });
    attachSentryUser(user);
    usePermissionsStore.getState().clearPermissions();
    usePermissionsStore.getState().fetchPermissions();
  },

  // Update token after silent refresh — no permission re-fetch needed
  updateToken: (accessToken, idToken) => {
    try {
      const user = applyRoleOverride(extractUser(accessToken, idToken));
      set({ token: accessToken, user, isAuthenticated: true });
      attachSentryUser(user);
    } catch {
      // If token parsing fails, leave state unchanged
    }
  },

  clearAuth: () => {
    set({ token: null, user: null, isAuthenticated: false });
    attachSentryUser(null);
  },

  hasRole: (role) => {
    const { user } = get();
    if (!user) return false;
    // In production, check dev-role-override first (admin role-switching)
    const override = localStorage.getItem('dev-role-override');
    if (override) {
      try {
        const parsed = JSON.parse(override);
        if (Array.isArray(parsed.roles)) return parsed.roles.includes(role);
      } catch { /* fall through */ }
    }
    return user.roles.includes(role);
  },
}));
